import { IEntityStats, AIArchetype } from '../types/entity.types';
import type { Element } from '../types/enums';
import type { EffectStep } from '../types/effect.types';

// ---------------------------------------------------------------------------
// Scaling curves
// ---------------------------------------------------------------------------

export type ScalingCurve = 'linear' | 'late_bloomer' | 'early_peak';

const SCALING_FUNCTIONS: Record<
  ScalingCurve,
  (base: number, level: number, rate: number) => number
> = {
  /** stat = base * (1 + (level-1) * rate) */
  linear: (base, level, rate) => Math.round(base * (1 + (level - 1) * rate)),

  /** Weak early, strong mid-to-late: stat = base * (1 + rate * (level-1)^1.5 / 4) */
  late_bloomer: (base, level, rate) =>
    Math.round(base * (1 + (rate * Math.pow(level - 1, 1.5)) / 4)),

  /** Strong early, plateaus later: stat = base * (1 + rate * sqrt(level-1)) */
  early_peak: (base, level, rate) =>
    Math.round(base * (1 + rate * Math.sqrt(level - 1))),
};

/** Default per-stat scaling rates. */
export const DEFAULT_STAT_RATES: Record<string, number> = {
  life: 0.12,
  physicalAttack: 0.08,
  magicAttack: 0.08,
  physicalDefense: 0.06,
  magicDefense: 0.06,
};

/** Apply level scaling using the monster's curve. */
export function scaleMonsterStat(
  base: number,
  level: number,
  rate: number,
  curve: ScalingCurve = 'linear',
): number {
  return SCALING_FUNCTIONS[curve](base, level, rate);
}

// ---------------------------------------------------------------------------
// Loot
// ---------------------------------------------------------------------------

/** A loot table entry — inventory items only. */
export interface LootEntry {
  itemId: string;
  quantityMin: number;
  quantityMax: number;
  chance: number;
  /**
   * Boss/mini-boss reward entry: drops at 100% on the player's first-ever kill
   * of this monster, then at the normal `chance` afterwards. Honored by
   * `rollIndependentPool` when the loot roll is flagged `firstKill`.
   */
  guaranteedOnFirstKill?: boolean;
}

// ---------------------------------------------------------------------------
// Shared loot tables
// ---------------------------------------------------------------------------

/** Standard loot for regular mobs and type2 monsters. */
export const MOB_LOOT: LootEntry[] = [
  { itemId: 'gold', quantityMin: 1, quantityMax: 3, chance: 0.5 },
  { itemId: 'pill-life', quantityMin: 1, quantityMax: 1, chance: 0.05 },
  {
    itemId: 'grimoire-tear-line',
    quantityMin: 1,
    quantityMax: 1,
    chance: 0.01,
  },
  {
    itemId: 'grimoire-creeping-dread',
    quantityMin: 1,
    quantityMax: 1,
    chance: 0.01,
  },
  {
    itemId: 'grimoire-decay-cloud',
    quantityMin: 1,
    quantityMax: 1,
    chance: 0.01,
  },
  { itemId: 'grimoire-repulse', quantityMin: 1, quantityMax: 1, chance: 0.01 },
];

/** Extra loot for elite mobs (stacks with MOB_LOOT). */
export const ELITE_LOOT: LootEntry[] = [
  { itemId: 'soul-shard', quantityMin: 1, quantityMax: 1, chance: 1 },
  { itemId: 'pill-life', quantityMin: 1, quantityMax: 1, chance: 0.2 },
];

/** Standard loot for mini-bosses and bosses. */
export const BOSS_LOOT: LootEntry[] = [
  { itemId: 'gold', quantityMin: 15, quantityMax: 30, chance: 1 },
  { itemId: 'pill-life', quantityMin: 1, quantityMax: 1, chance: 0.5 },
];

/**
 * Boss loot = the shared `BOSS_LOOT` plus the boss's signature-spell grimoire,
 * which drops 100% on the first kill and 25% on later kills (cf.
 * `LootEntry.guaranteedOnFirstKill`).
 */
function bossLootWithGrimoire(spellId: string): LootEntry[] {
  return [
    ...BOSS_LOOT,
    {
      itemId: `grimoire-${spellId}`,
      quantityMin: 1,
      quantityMax: 1,
      chance: 0.25,
      guaranteedOnFirstKill: true,
    },
  ];
}

// ---------------------------------------------------------------------------
// Elite scaling
// ---------------------------------------------------------------------------

/**
 * Modifiers applied to a mob instance flagged as elite (cf.
 * `MonsterEntity.makeElite`).
 */
export interface EliteScaling {
  statMultipliers?: {
    life?: number;
    physicalAttack?: number;
    physicalDefense?: number;
    magicAttack?: number;
    magicDefense?: number;
    moveSpeed?: number;
  };
  xpMultiplier?: number;
  /** Visual buff applied on spawn. Default `'elite-aura'`. */
  visualBuffId?: string;
}

export const DEFAULT_ELITE_SCALING: EliteScaling = {
  statMultipliers: {
    life: 2.5,
    physicalAttack: 1.6,
    physicalDefense: 1.25,
    magicAttack: 1.6,
    magicDefense: 1.25,
  },
  xpMultiplier: 3,
  visualBuffId: 'elite-aura',
};

// ---------------------------------------------------------------------------
// Monster base definitions
// ---------------------------------------------------------------------------

/** Map/minimap marker kind. Extensible — add a value here + a MONSTER_ICON_STYLE
 *  entry to give a special monster type its own marker. */
export type MonsterMapIcon = 'normal' | 'elite' | 'miniboss' | 'boss';

export type MonsterSpellEntry =
  | string
  | { spellId: string; cooldownModifier?: number; castTimeModifier?: number };

export interface MonsterPhase {
  hpThreshold: number;
  spellIds: string[];
  onEnter?: EffectStep[];
}

export interface MonsterBaseConfig {
  configId: string;
  /** Pokédex-style number for the bestiary binder. Unique, 1-based. */
  bestiaryId: number;
  /** Icons-atlas frame name for the bestiary card portrait. Placeholder if absent. */
  icon?: string;
  /** Defensive emotion-type(s), 0-2. Drives the effectiveness chart. */
  emotionTypes?: Element[];
  label: string;
  life: number;
  baseStats: Partial<IEntityStats>;
  scalingCurve: ScalingCurve;
  xpBase: number;
  isWalkableOver: boolean;
  /** Whether knockback effects can displace this monster. Default true. */
  knockbackable?: boolean;
  /** Loot table — independent rolls per entry. */
  loot: LootEntry[];
  /** 1–4 spells (non-boss). Plain id, or an object carrying per-spell
   * cast-time / cooldown modifiers (100 = neutral, 150 = +50%). */
  spellIds: MonsterSpellEntry[];
  /** Combat behavior. Default 'melee'. Drives positioning preference and the
   * no-spell fallback (cf. docs/ai.md). */
  aiArchetype?: AIArchetype;
  initialBuffs?: string[];
  phases?: MonsterPhase[];
  /** SFX played when the monster takes damage (cf. SFX_REGISTRY). */
  onHitSfx?: string;
  /**
   * True if this monster can be promoted to elite on spawn (cf.
   * `MonsterSpawningService.createMonster` and `FloorConfig.eliteChance`).
   * Bosses and mini-bosses keep this false. Default false.
   */
  eligibleForElite?: boolean;
  /**
   * True if this monster is a mini-boss (cf. docs/game-design/run-loop.md
   * "Boss et mini-bosses"). Informational flag for bestiary / UI.
   */
  isMiniBoss?: boolean;
  /**
   * Loot dropped **only** if the monster is killed while elite. Stacks with
   * the standard `loot[]`. Each entry's `chance` allows partial drops:
   * `chance: 1` = guaranteed, `chance: 0.3` = elite-only at 30%.
   * Cf. docs/game-design/loot.md "Loot de pointe".
   */
  eliteLoot?: LootEntry[];
  /** Map marker kind for this monster type (default 'normal'). Elite/mini-boss/
   *  boss are set at runtime by promotion/spawn context; set this only for a
   *  special always-on type. */
  mapIcon?: MonsterMapIcon;
  /** HP ratio threshold (0–1) below which the monster enters FLEE state. */
  fleeBelowHpPercent?: number;
  /**
   * Minimum delay (ms) between two offensive actions, on top of spell
   * cooldowns and action lag. Paces the AI so a monster does not dump its whole
   * kit at once and stand idle afterwards. Falls back to
   * `DEFAULT_AI_ACTION_INTERVAL_MS`.
   */
  actionIntervalMs?: number;
}

/** Default AI offensive-action spacing (ms) when a monster sets no override. */
export const DEFAULT_AI_ACTION_INTERVAL_MS = 700;

export function spellEntryId(e: MonsterSpellEntry): string {
  return typeof e === 'string' ? e : e.spellId;
}

export function spellEntryModifiers(e: MonsterSpellEntry): {
  cooldownModifier: number;
  castTimeModifier: number;
} {
  if (typeof e === 'string') {
    return { cooldownModifier: 100, castTimeModifier: 100 };
  }
  return {
    cooldownModifier: e.cooldownModifier ?? 100,
    castTimeModifier: e.castTimeModifier ?? 100,
  };
}

export const MONSTER_CONFIGS: Record<string, MonsterBaseConfig> = {
  // ---------------------------------------------------------------------------
  // Regular monsters
  // ---------------------------------------------------------------------------

  frog: {
    configId: 'frog',
    bestiaryId: 42,
    label: 'Grenouille',
    emotionTypes: ['joy'],
    life: 16,
    baseStats: {
      spellCastTime: 1.3,
      spellCooldown: 1.3,
      physicalAttack: 7,
      magicAttack: 4,
      physicalDefense: 3,
      magicDefense: 3,
      moveSpeed: 120,
    },
    scalingCurve: 'early_peak',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['joyful-rush'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  dragonfly: {
    configId: 'dragonfly',
    bestiaryId: 41,
    label: 'Libellule',
    emotionTypes: ['surprise'],
    life: 16,
    baseStats: {
      spellCastTime: 1.3,
      spellCooldown: 1.3,
      physicalAttack: 6,
      magicAttack: 10,
      physicalDefense: 2,
      magicDefense: 2,
      moveSpeed: 130,
    },
    scalingCurve: 'early_peak',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['startle', 'gale-cone'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  ducky: {
    configId: 'ducky',
    bestiaryId: 38,
    label: 'Caneton',
    emotionTypes: ['joy'],
    life: 18,
    baseStats: {
      spellCastTime: 1.3,
      spellCooldown: 1.3,
      physicalAttack: 4,
      magicAttack: 6,
      physicalDefense: 5,
      magicDefense: 5,
      moveSpeed: 100,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['radiance'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  beaver: {
    configId: 'beaver',
    bestiaryId: 40,
    label: 'Castor',
    emotionTypes: ['sadness'],
    life: 35,
    baseStats: {
      spellCastTime: 1.3,
      spellCooldown: 1.3,
      physicalAttack: 2,
      magicAttack: 4,
      physicalDefense: 10,
      magicDefense: 8,
      moveSpeed: 80,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['gloom', 'tear-line'],
    aiArchetype: 'melee',
    eligibleForElite: true,
  },

  bat: {
    configId: 'bat',
    bestiaryId: 39,
    label: 'Chauve-souris',
    emotionTypes: ['surprise', 'fear'],
    life: 12,
    baseStats: {
      spellCastTime: 1.3,
      spellCooldown: 1.3,
      physicalAttack: 6,
      magicAttack: 10,
      physicalDefense: 2,
      magicDefense: 2,
      moveSpeed: 130,
    },
    scalingCurve: 'early_peak',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['startle', 'creeping-dread'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  worm: {
    configId: 'worm',
    bestiaryId: 46,
    label: 'Ver',
    emotionTypes: ['disgust'],
    life: 22,
    baseStats: {
      spellCastTime: 1.3,
      spellCooldown: 1.3,
      physicalAttack: 8,
      magicAttack: 5,
      physicalDefense: 6,
      magicDefense: 2,
      moveSpeed: 90,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['venom-bite', 'repulse'],
    aiArchetype: 'melee',
    eligibleForElite: true,
  },

  molerat: {
    configId: 'molerat',
    bestiaryId: 43,
    label: 'Rat-taupe',
    emotionTypes: ['disgust', 'anger'],
    life: 22,
    baseStats: {
      spellCastTime: 1.3,
      spellCooldown: 1.3,
      physicalAttack: 8,
      magicAttack: 5,
      physicalDefense: 6,
      magicDefense: 2,
      moveSpeed: 90,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['venom-bite', 'outburst'],
    aiArchetype: 'melee',
    eligibleForElite: true,
  },

  'shoebill-chick': {
    configId: 'shoebill-chick',
    bestiaryId: 59,
    label: 'Bébé bec-en-sabot',
    emotionTypes: ['sadness'],
    life: 16,
    baseStats: {
      spellCastTime: 1.3,
      spellCooldown: 1.3,
      physicalAttack: 4,
      magicAttack: 7,
      physicalDefense: 3,
      magicDefense: 3,
      moveSpeed: 120,
    },
    scalingCurve: 'early_peak',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['tear-line'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  knight: {
    configId: 'knight',
    bestiaryId: 34,
    label: 'Chevalier',
    emotionTypes: ['fear'],
    life: 22,
    baseStats: {
      spellCastTime: 1.8,
      spellCooldown: 1.3,
      physicalAttack: 8,
      magicAttack: 8,
      physicalDefense: 6,
      magicDefense: 2,
      moveSpeed: 90,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['fear-strike', 'paralyze'],
    aiArchetype: 'melee',
    eligibleForElite: true,
  },

  lizard: {
    configId: 'lizard',
    bestiaryId: 30,
    label: 'Lézard',
    emotionTypes: ['anger'],
    life: 12,
    baseStats: {
      spellCastTime: 1.3,
      spellCooldown: 1.3,
      physicalAttack: 10,
      magicAttack: 6,
      physicalDefense: 2,
      magicDefense: 2,
      moveSpeed: 130,
      size: 180,
    },
    scalingCurve: 'early_peak',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['outburst', 'charge'],
    aiArchetype: 'melee',
    eligibleForElite: true,
  },

  fiend: {
    configId: 'fiend',
    bestiaryId: 28,
    label: 'Suppôt',
    emotionTypes: ['anger', 'disgust'],
    life: 12,
    baseStats: {
      spellCastTime: 1.3,
      spellCooldown: 1.3,
      physicalAttack: 10,
      magicAttack: 6,
      physicalDefense: 2,
      magicDefense: 2,
      moveSpeed: 130,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['outburst', 'venom-bite'],
    aiArchetype: 'melee',
    eligibleForElite: true,
  },

  spider: {
    configId: 'spider',
    bestiaryId: 60,
    label: 'Araignée',
    emotionTypes: ['disgust'],
    life: 16,
    baseStats: {
      spellCastTime: 1.3,
      spellCooldown: 1.3,
      physicalAttack: 7,
      magicAttack: 4,
      physicalDefense: 3,
      magicDefense: 3,
      moveSpeed: 120,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['venom-bite', 'decay-cloud'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  watcher: {
    configId: 'watcher',
    bestiaryId: 64,
    label: 'Guetteur',
    emotionTypes: ['fear'],
    life: 12,
    baseStats: {
      spellCastTime: 1.3,
      spellCooldown: 1.3,
      physicalAttack: 6,
      magicAttack: 10,
      physicalDefense: 2,
      magicDefense: 5,
      moveSpeed: 100,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['paralyze', 'creeping-dread'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  whisper: {
    configId: 'whisper',
    bestiaryId: 65,
    label: 'Murmure',
    emotionTypes: ['fear', 'sadness'],
    life: 18,
    baseStats: {
      spellCastTime: 1.3,
      spellCooldown: 1.3,
      physicalAttack: 4,
      magicAttack: 6,
      physicalDefense: 5,
      magicDefense: 5,
      moveSpeed: 100,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['creeping-dread', 'gloom'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  'small-dragon': {
    configId: 'small-dragon',
    bestiaryId: 32,
    label: 'Petit dragon',
    emotionTypes: ['anger', 'surprise'],
    life: 12,
    baseStats: {
      physicalAttack: 10,
      magicAttack: 6,
      physicalDefense: 2,
      magicDefense: 2,
      moveSpeed: 130,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['outburst', 'startle'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  rhino: {
    configId: 'rhino',
    bestiaryId: 44,
    label: 'Rhinocéros',
    emotionTypes: ['anger'],
    life: 22,
    baseStats: {
      physicalAttack: 8,
      magicAttack: 5,
      physicalDefense: 6,
      magicDefense: 2,
      moveSpeed: 90,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['charge', 'outburst'],
    aiArchetype: 'melee',
    eligibleForElite: true,
  },

  cell: {
    configId: 'cell',
    bestiaryId: 36,
    label: 'Cellule',
    emotionTypes: ['disgust'],
    life: 18,
    baseStats: {
      physicalAttack: 4,
      magicAttack: 6,
      physicalDefense: 5,
      magicDefense: 5,
      moveSpeed: 100,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['decay-cloud', 'drain'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  demon: {
    configId: 'demon',
    bestiaryId: 61,
    label: 'Démon',
    emotionTypes: ['anger', 'fear'],
    life: 12,
    baseStats: {
      physicalAttack: 10,
      magicAttack: 6,
      physicalDefense: 2,
      magicDefense: 2,
      moveSpeed: 130,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['outburst', 'creeping-dread'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  hydra: {
    configId: 'hydra',
    bestiaryId: 62,
    label: 'Hydre',
    emotionTypes: ['sadness', 'disgust'],
    life: 35,
    baseStats: {
      physicalAttack: 2,
      magicAttack: 4,
      physicalDefense: 10,
      magicDefense: 8,
      moveSpeed: 80,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['gloom', 'drain'],
    aiArchetype: 'melee',
    eligibleForElite: true,
  },

  phoenixling: {
    configId: 'phoenixling',
    bestiaryId: 37,
    label: 'Phéniceau',
    emotionTypes: ['joy'],
    life: 12,
    baseStats: {
      physicalAttack: 6,
      magicAttack: 10,
      physicalDefense: 2,
      magicDefense: 5,
      moveSpeed: 100,
    },
    scalingCurve: 'early_peak',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['radiance', 'joyful-rush'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  'dino-rex': {
    configId: 'dino-rex',
    bestiaryId: 35,
    label: 'Dino Rex',
    emotionTypes: ['anger'],
    life: 22,
    baseStats: {
      physicalAttack: 8,
      magicAttack: 5,
      physicalDefense: 6,
      magicDefense: 2,
      moveSpeed: 90,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['charge', 'outburst'],
    aiArchetype: 'melee',
    eligibleForElite: true,
  },

  astronaut: {
    configId: 'astronaut',
    bestiaryId: 58,
    label: 'Astronaute',
    emotionTypes: ['surprise'],
    life: 16,
    baseStats: {
      physicalAttack: 4,
      magicAttack: 7,
      physicalDefense: 3,
      magicDefense: 3,
      moveSpeed: 120,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['gale-cone', 'startle'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  // ---------------------------------------------------------------------------
  // Type2 monsters
  // ---------------------------------------------------------------------------

  'type2-00': {
    configId: 'type2-00',
    bestiaryId: 47,
    label: 'Type2 00',
    emotionTypes: ['joy'],
    life: 16,
    baseStats: {
      physicalAttack: 7,
      magicAttack: 4,
      physicalDefense: 3,
      magicDefense: 3,
      moveSpeed: 120,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['joyful-rush'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  'type2-01': {
    configId: 'type2-01',
    bestiaryId: 48,
    label: 'Type2 01',
    emotionTypes: ['sadness'],
    life: 12,
    baseStats: {
      physicalAttack: 6,
      magicAttack: 10,
      physicalDefense: 2,
      magicDefense: 5,
      moveSpeed: 100,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['tear-line'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  'type2-02': {
    configId: 'type2-02',
    bestiaryId: 49,
    label: 'Type2 02',
    emotionTypes: ['anger'],
    life: 12,
    baseStats: {
      physicalAttack: 10,
      magicAttack: 6,
      physicalDefense: 2,
      magicDefense: 2,
      moveSpeed: 130,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['outburst'],
    aiArchetype: 'melee',
    eligibleForElite: true,
  },

  'type2-03': {
    configId: 'type2-03',
    bestiaryId: 50,
    label: 'Type2 03',
    emotionTypes: ['fear'],
    life: 12,
    baseStats: {
      physicalAttack: 6,
      magicAttack: 10,
      physicalDefense: 2,
      magicDefense: 5,
      moveSpeed: 100,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['paralyze'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  'type2-04': {
    configId: 'type2-04',
    bestiaryId: 51,
    label: 'Type2 04',
    emotionTypes: ['surprise'],
    life: 16,
    baseStats: {
      physicalAttack: 4,
      magicAttack: 7,
      physicalDefense: 3,
      magicDefense: 3,
      moveSpeed: 120,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['startle'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  'type2-05': {
    configId: 'type2-05',
    bestiaryId: 52,
    label: 'Type2 05',
    emotionTypes: ['disgust'],
    life: 22,
    baseStats: {
      physicalAttack: 8,
      magicAttack: 5,
      physicalDefense: 6,
      magicDefense: 2,
      moveSpeed: 90,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['venom-bite'],
    aiArchetype: 'melee',
    eligibleForElite: true,
  },

  'type2-06': {
    configId: 'type2-06',
    bestiaryId: 53,
    label: 'Type2 06',
    emotionTypes: ['joy', 'anger'],
    life: 18,
    baseStats: {
      physicalAttack: 7,
      magicAttack: 7,
      physicalDefense: 5,
      magicDefense: 5,
      moveSpeed: 100,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['radiance', 'outburst'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  'type2-07': {
    configId: 'type2-07',
    bestiaryId: 54,
    label: 'Type2 07',
    emotionTypes: ['sadness', 'fear'],
    life: 18,
    baseStats: {
      physicalAttack: 4,
      magicAttack: 6,
      physicalDefense: 5,
      magicDefense: 5,
      moveSpeed: 100,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['creeping-dread', 'gloom'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  'type2-08': {
    configId: 'type2-08',
    bestiaryId: 55,
    label: 'Type2 08',
    emotionTypes: ['surprise', 'disgust'],
    life: 16,
    baseStats: {
      physicalAttack: 4,
      magicAttack: 7,
      physicalDefense: 3,
      magicDefense: 3,
      moveSpeed: 120,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['gale-cone', 'drain'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  'type2-09': {
    configId: 'type2-09',
    bestiaryId: 56,
    label: 'Type2 09',
    emotionTypes: ['anger'],
    life: 22,
    baseStats: {
      physicalAttack: 8,
      magicAttack: 5,
      physicalDefense: 6,
      magicDefense: 2,
      moveSpeed: 90,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['charge'],
    aiArchetype: 'melee',
    eligibleForElite: true,
  },

  'type2-10': {
    configId: 'type2-10',
    bestiaryId: 57,
    label: 'Type2 10',
    emotionTypes: ['disgust'],
    life: 18,
    baseStats: {
      physicalAttack: 4,
      magicAttack: 6,
      physicalDefense: 5,
      magicDefense: 5,
      moveSpeed: 100,
    },
    scalingCurve: 'linear',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: true,
    loot: MOB_LOOT,
    eliteLoot: ELITE_LOOT,
    spellIds: ['decay-cloud'],
    aiArchetype: 'ranged',
    eligibleForElite: true,
  },

  // ---------------------------------------------------------------------------
  // Mini-bosses
  // ---------------------------------------------------------------------------

  kitsune: {
    configId: 'kitsune',
    bestiaryId: 63,
    label: 'Kitsune',
    emotionTypes: ['surprise'],
    life: 45,
    baseStats: {
      physicalAttack: 7,
      magicAttack: 11,
      physicalDefense: 7,
      magicDefense: 6,
      moveSpeed: 100,
    },
    scalingCurve: 'late_bloomer',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: false,
    loot: bossLootWithGrimoire('ambush'),
    spellIds: ['kitsune-foxfire', 'kitsune-blink', 'kitsune-illusion'],
    aiArchetype: 'ranged',
    eligibleForElite: false,
    isMiniBoss: true,
    fleeBelowHpPercent: 0.3,
  },

  jinn: {
    configId: 'jinn',
    bestiaryId: 33,
    label: 'Djinn',
    emotionTypes: ['surprise', 'anger'],
    life: 45,
    baseStats: {
      physicalAttack: 7,
      magicAttack: 11,
      physicalDefense: 7,
      magicDefense: 6,
      moveSpeed: 100,
    },
    scalingCurve: 'late_bloomer',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: false,
    loot: bossLootWithGrimoire('euphoria'),
    spellIds: ['jinn-gale', 'jinn-emberbolt', 'jinn-mirage'],
    aiArchetype: 'ranged',
    eligibleForElite: false,
    isMiniBoss: true,
  },

  parasite: {
    configId: 'parasite',
    bestiaryId: 66,
    label: 'Parasite',
    emotionTypes: ['disgust'],
    life: 45,
    baseStats: {
      physicalAttack: 7,
      magicAttack: 11,
      physicalDefense: 7,
      magicDefense: 6,
      moveSpeed: 100,
    },
    scalingCurve: 'late_bloomer',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: false,
    loot: bossLootWithGrimoire('plague'),
    spellIds: ['parasite-spores', 'parasite-drain', 'parasite-infest'],
    aiArchetype: 'melee',
    eligibleForElite: false,
    isMiniBoss: true,
  },

  // ---------------------------------------------------------------------------
  // Bosses
  // ---------------------------------------------------------------------------

  shoebill: {
    configId: 'shoebill',
    bestiaryId: 45,
    label: 'Bec-en-sabot',
    emotionTypes: ['sadness'],
    life: 70,
    baseStats: {
      physicalAttack: 8,
      magicAttack: 14,
      physicalDefense: 9,
      magicDefense: 8,
      moveSpeed: 95,
    },
    scalingCurve: 'late_bloomer',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: false,
    loot: bossLootWithGrimoire('despair'),
    spellIds: ['shoebill-peck', 'shoebill-screech'],
    aiArchetype: 'ranged',
    eligibleForElite: false,
    phases: [
      { hpThreshold: 1.0, spellIds: [] },
      {
        hpThreshold: 0.4,
        spellIds: ['shoebill-summon-chicks'],
        onEnter: [
          {
            handler: 'spawnMonsters',
            params: { monster: 'shoebill-chick', count: 2 },
          },
          { handler: 'setStats', params: { stat: 'moveSpeed', value: 115 } },
        ],
      },
    ],
  },

  medusa: {
    configId: 'medusa',
    bestiaryId: 31,
    label: 'Méduse',
    emotionTypes: ['fear'],
    life: 70,
    baseStats: {
      physicalAttack: 14,
      magicAttack: 14,
      physicalDefense: 9,
      magicDefense: 8,
      moveSpeed: 95,
    },
    scalingCurve: 'late_bloomer',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: false,
    loot: bossLootWithGrimoire('terror'),
    spellIds: ['medusa-gaze', 'medusa-lash'],
    aiArchetype: 'melee',
    eligibleForElite: false,
    phases: [
      { hpThreshold: 1.0, spellIds: [] },
      {
        hpThreshold: 0.5,
        spellIds: ['medusa-petrify'],
        onEnter: [
          { handler: 'setStats', params: { stat: 'moveSpeed', value: 115 } },
        ],
      },
    ],
  },

  dragon: {
    configId: 'dragon',
    bestiaryId: 29,
    label: 'Dragon',
    emotionTypes: ['anger', 'fear'],
    life: 90,
    baseStats: {
      physicalAttack: 16,
      magicAttack: 16,
      physicalDefense: 10,
      magicDefense: 9,
      moveSpeed: 95,
    },
    scalingCurve: 'late_bloomer',
    xpBase: 10,
    isWalkableOver: false,
    knockbackable: false,
    loot: bossLootWithGrimoire('wrath'),
    spellIds: ['dragon-claw', 'dragon-breath'],
    aiArchetype: 'melee',
    eligibleForElite: false,
    phases: [
      { hpThreshold: 1.0, spellIds: [] },
      { hpThreshold: 0.6, spellIds: ['dragon-roar'] },
      {
        hpThreshold: 0.3,
        spellIds: ['dragon-wingstorm'],
        onEnter: [
          {
            handler: 'setStats',
            params: { stat: 'physicalAttack', value: 28 },
          },
          { handler: 'setStats', params: { stat: 'moveSpeed', value: 110 } },
        ],
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Chest/urn Location loot table
// ---------------------------------------------------------------------------

export const MAP_LOOT_TABLE: LootEntry[] = [
  { itemId: 'pill-life', quantityMin: 1, quantityMax: 2, chance: 0.3 },
  { itemId: 'pill-spirit', quantityMin: 1, quantityMax: 1, chance: 0.25 },
  { itemId: 'antidote', quantityMin: 1, quantityMax: 1, chance: 0.15 },
  { itemId: 'thunder-in-a-box', quantityMin: 1, quantityMax: 1, chance: 0.1 },
  { itemId: 'elixir-swift', quantityMin: 1, quantityMax: 1, chance: 0.05 },
];
